import type { Condition as RuntimeCondition } from "@skilitics/judy";
import type { ConditionBuild } from "@skilitics/nautilus-core-designer-data";

import type { GetConditionBuildable } from "../../triggers/index.ts";
import type { BuildRuntimeChildConditionOptions, RuntimeConditionChild } from "../../types/index.ts";
import type { ResolveValueToConditionValue } from "../../values/index.ts";

export const buildRuntimeConditionCreate = (
  getConditionBuildable: GetConditionBuildable,
  resolveConditionValue: ResolveValueToConditionValue,
) => {
  // just because a buildable exists in the data doesn't mean it's
  // available, a node designer that produced it can veto it by not returning it in getConditionBuildables()
  const isAvailableToBuild = (b: ConditionBuild) => getConditionBuildable({ nodeId: b.nodeId, name: b.buildableName });

  return (root: ConditionBuild): RuntimeCondition | undefined => {
    const buildRuntimeCondition = (
      { nodeId, buildableName, config, children: childBuildables }: ConditionBuild,
      { propertyMode = "nodeResult" }: BuildRuntimeChildConditionOptions = {},
    ): RuntimeCondition | undefined => {
      const buildable = getConditionBuildable({ nodeId, name: buildableName });
      if (!buildable) {
        // node designers control which conditions are available via getConditionBuildables and in some cases
        // the data is there (the build) but the condition isn't appropriate for other reasons
        // so it is not made available (i.e. not returned by getConditionBuildables).
        return undefined;
      }

      const buildChildBuildFn = (build: ConditionBuild): RuntimeConditionChild => ({
        build: (options) => buildRuntimeCondition(build, options),
      });

      const children = Object.fromEntries(
        Object.entries(childBuildables).map(([name, bOrbs]) => [
          name,
          Array.isArray(bOrbs)
            ? bOrbs
                .filter(isAvailableToBuild)
                .map(buildChildBuildFn)
                .filter((b): b is NonNullable<typeof b> => !!b)
            : bOrbs && isAvailableToBuild(bOrbs)
              ? buildChildBuildFn(bOrbs)
              : undefined,
        ]),
      );
      const { spec, builder } = buildable;
      return builder.build({
        nodeId,
        config,
        spec,
        // @ts-ignore
        resolvableValue: resolveConditionValue,
        // @ts-ignore
        children,
        // @ts-ignore
        envProperty: (name, type) => ({
          type,
          resolveType: "envProperty",
          name,
        }),
        // TODO: rename e.g. to "property" as not necessarily only result property anymore
        resultProperty: (name, type) =>
          // @ts-ignore
          propertyMode === "nodeResult"
            ? {
                type,
                resolveType: "nodeResultProperty",
                name,
                nodeId, // if not specified, it will use the nodeId the trigger is on which is normally not what we want
              }
            : {
                type,
                resolveType: "objectProperty",
                name,
              },
      });
    };

    return buildRuntimeCondition(root);
  };
};

export type BuildRuntimeCondition = ReturnType<typeof buildRuntimeConditionCreate>;
